// ManDana - #book organizer orchestration (Phase 1B-3)
//
// Records keep their writing. This module derives the canonical #book trigger,
// ensures its membership edge, asks the authenticated provider for candidates,
// and hands every mutation to md-organizer. It never writes Record, database,
// View, Form, or Manifestation state.

interface MdBookSnapshot {
  refKey: string;
  sourceKey: string;
  text: string;
  locale?: string;
}

interface MdBookRunState {
  status: 'idle' | 'pending' | 'running' | 'ready' | 'failed' | 'stale';
  detail?: string;
  token?: number;
  fingerprint?: string;
}

(function (g: any) {
  'use strict';

  const LIBRARY_ID = 'libbook01';
  const SCHEMA_ID = 'schbk001';
  const POLICY_ID = 'polbk001';
  const BOOK_RULE = 'book-enrichment';
  const SOURCE_KEY_RE = /^(?:events|records):\d{4}-\d{2}-\d{2}$|^record:[a-z0-9]{6,40}$|^project:[a-z0-9]{6,40}$|^project-records$|^domain:e\d{1,3}$|^pursuit:[a-z0-9]{6,40}$/;
  const RUN_ID_RE = /^organizer-run:[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  const MAX_EXCERPT_BYTES = 12 * 1024;
  const UTF8 = new TextEncoder();

  const BOOK_SCHEMA = Object.freeze({
    id: SCHEMA_ID, tag: 'book', v: 2,
    properties: Object.freeze([
      Object.freeze({ id: 'rec:supertag:book/title', type: 'text', label: 'Book title' }),
      Object.freeze({ id: 'rec:supertag:book/author-text', type: 'text', label: 'Author' }),
      Object.freeze({ id: 'rec:supertag:book/published-date', type: 'date', label: 'Published date' }),
      Object.freeze({ id: 'rec:supertag:book/source-url', type: 'url', label: 'Source link' }),
      Object.freeze({ id: 'rec:supertag:book/authors', type: 'record-ref-list', label: 'Authors' }),
    ]),
    at: 1,
  });
  const PROVIDER_PROPERTIES = BOOK_SCHEMA.properties.filter((item) =>
    item.id !== 'rec:supertag:book/authors');
  const BOOK_POLICY = Object.freeze({
    id: POLICY_ID, scope: 'book', v: 1, autofill: 0.85, at: 1,
  });

  const snapshots = new Map<string, MdBookSnapshot>();
  const runs = new Map<string, MdBookRunState>();
  const controllers = new Map<string, AbortController>();
  const observedRefs = new Set<string>();
  let runSeq = 0;

  const same = (a: unknown, b: unknown): boolean => JSON.stringify(a) === JSON.stringify(b);
  const hasBookTag = (text: string): boolean =>
    Array.isArray(g.mdTags?.(text)) && g.mdTags(text).some((tag: { key?: unknown }) => tag.key === 'book');

  function emit(refKey: string): void {
    if (typeof g.dispatchEvent === 'function' && typeof g.CustomEvent === 'function') {
      g.dispatchEvent(new g.CustomEvent('mdbookchange', { detail: { refKey } }));
    }
  }

  function setRun(refKey: string, state: MdBookRunState): MdBookRunState {
    runs.set(refKey, state);
    emit(refKey);
    return state;
  }

  function cleanSnapshot(value: unknown): MdBookSnapshot | null {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
    const raw = value as Record<string, unknown>;
    if (typeof raw.refKey !== 'string' || !g.mdRecordRefParse?.(raw.refKey)
      || typeof raw.sourceKey !== 'string' || !SOURCE_KEY_RE.test(raw.sourceKey)
      || typeof raw.text !== 'string' || !raw.text.length) return null;
    const out: MdBookSnapshot = {
      refKey: raw.refKey, sourceKey: raw.sourceKey, text: raw.text,
    };
    if (typeof raw.locale === 'string' && raw.locale.length <= 35) out.locale = raw.locale;
    return out;
  }

  function mdBookFingerprint(value: unknown): string | null {
    const snapshot = cleanSnapshot(value);
    if (!snapshot) return null;
    const tags = g.mdTags(snapshot.text).map((tag: { key: string }) => tag.key).sort();
    const canonical = JSON.stringify({ ref: snapshot.refKey, text: snapshot.text, tags });
    let h1 = 5381; let h2 = 52711;
    for (let i = 0; i < canonical.length; i++) {
      h1 = ((h1 << 5) + h1 + canonical.charCodeAt(i)) | 0;
      h2 = ((h2 << 5) + h2 ^ canonical.charCodeAt(i)) | 0;
    }
    return 'body:' + (h1 >>> 0).toString(36) + (h2 >>> 0).toString(36);
  }

  function excerpt(text: string): string {
    if (UTF8.encode(text).byteLength <= MAX_EXCERPT_BYTES) return text;
    let low = 0; let high = text.length;
    while (low < high) {
      const mid = Math.ceil((low + high) / 2);
      if (UTF8.encode(text.slice(0, mid)).byteLength <= MAX_EXCERPT_BYTES) low = mid;
      else high = mid - 1;
    }
    return text.slice(0, low);
  }

  function mdBookBootstrap(): { ok: true } | { ok: false; reason: string } {
    const existingSchema = g.mdOrganizerSchemaGet?.(SCHEMA_ID);
    const schemaMatches = existingSchema && existingSchema.id === BOOK_SCHEMA.id
      && existingSchema.tag === BOOK_SCHEMA.tag && existingSchema.v === BOOK_SCHEMA.v
      && same(existingSchema.properties, BOOK_SCHEMA.properties);
    if (existingSchema && !schemaMatches) {
      return { ok: false, reason: 'SCHEMA_MISMATCH' };
    }
    if (!existingSchema && !g.mdOrganizerSchemaSave?.(BOOK_SCHEMA)) {
      return { ok: false, reason: 'SCHEMA_MISMATCH' };
    }
    const existingPolicy = g.mdOrganizerPolicyGet?.(POLICY_ID);
    const policyMatches = existingPolicy && existingPolicy.id === BOOK_POLICY.id
      && existingPolicy.scope === BOOK_POLICY.scope && existingPolicy.v === BOOK_POLICY.v
      && existingPolicy.autofill === BOOK_POLICY.autofill;
    if (existingPolicy && !policyMatches) {
      return { ok: false, reason: 'POLICY_MISMATCH' };
    }
    if (!existingPolicy && !g.mdOrganizerPolicySave?.(BOOK_POLICY)) {
      return { ok: false, reason: 'POLICY_MISMATCH' };
    }
    return { ok: true };
  }

  /* A keyed lookup, not a scan. This runs once per record from
     mdBookReconcileAll at boot, and listing every edge on the device to find
     one of them is what made a 5,000-record account take 25 seconds to open.
     Falls back to the old shape if md-membership.ts predates the lookup. */
  function bookEdge(refKey: string): any | null {
    const entry = typeof g.mdMembershipFind === 'function'
      ? g.mdMembershipFind(LIBRARY_ID, refKey)
      : (g.mdMembershipList?.(LIBRARY_ID) || []).find((item: any) => item.k === refKey);
    return entry ? g.mdMembershipEdgeGet?.(entry.id) || null : null;
  }

  function bookEdgeId(refKey: string): string {
    const canonical = `${LIBRARY_ID}\u0000${refKey}`;
    let h1 = 5381; let h2 = 52711;
    for (let i = 0; i < canonical.length; i++) {
      h1 = ((h1 << 5) + h1 + canonical.charCodeAt(i)) | 0;
      h2 = ((h2 << 5) + h2 ^ canonical.charCodeAt(i)) | 0;
    }
    return 'bk' + (h1 >>> 0).toString(36).padStart(7, '0')
      + (h2 >>> 0).toString(36).padStart(7, '0');
  }

  function currentRev(key: string): number | null {
    const rev = typeof g.mdSyncDocCurrentRev === 'function'
      ? g.mdSyncDocCurrentRev(key) : g.mdSyncDocRev?.(key);
    return Number.isInteger(rev) && rev >= 0 ? rev : null;
  }

  function safeRef(value: unknown): string | null {
    if (typeof value !== 'string' || value.length > 300) return null;
    try {
      const url = new URL(value);
      if (url.protocol !== 'https:' || url.username || url.password) return null;
      url.hash = '';
      return url.toString();
    } catch (e) { return null; }
  }

  function providerEnvelope(value: unknown): { runId: string; candidates: any[] } | null {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
    const raw = value as Record<string, unknown>;
    if (Object.keys(raw).some((key) => !['runId', 'candidates', 'providerResponseId'].includes(key))
      || (raw.providerResponseId != null
        && (typeof raw.providerResponseId !== 'string' || raw.providerResponseId.length > 120))) return null;
    if (typeof raw.runId !== 'string' || !RUN_ID_RE.test(raw.runId)
      || !Array.isArray(raw.candidates) || raw.candidates.length > PROVIDER_PROPERTIES.length) return null;
    const allowed = new Set<string>(PROVIDER_PROPERTIES.map((item) => item.id));
    const seen = new Set<string>();
    const candidates = [];
    for (const row of raw.candidates) {
      if (!row || typeof row !== 'object' || Array.isArray(row)) continue;
      const item = row as Record<string, unknown>;
      if (Object.keys(item).some((key) => !['propertyId', 'candidate', 'prov'].includes(key))
        || typeof item.propertyId !== 'string' || !allowed.has(item.propertyId)
        || seen.has(item.propertyId) || !item.prov || typeof item.prov !== 'object') continue;
      const prov = item.prov as Record<string, unknown>;
      if (Object.keys(prov).some((key) => !['conf', 'model', 'prompt', 'refs'].includes(key))
        || typeof prov.conf !== 'number' || !Number.isFinite(prov.conf)
        || prov.conf < 0 || prov.conf > 1 || typeof prov.model !== 'string'
        || typeof prov.prompt !== 'string' || !Array.isArray(prov.refs)) continue;
      const refs = [...new Set(prov.refs.map(safeRef).filter(Boolean))];
      if (!refs.length) continue;
      const validated = g.mdOrganizerCandidateValidate?.(SCHEMA_ID, item.propertyId, item.candidate);
      if (!validated?.ok) continue;
      seen.add(item.propertyId);
      candidates.push({
        propertyId: item.propertyId, candidate: validated.value,
        prov: { conf: prov.conf, model: prov.model.slice(0, 120),
          prompt: prov.prompt.slice(0, 120), refs },
      });
    }
    return { runId: raw.runId, candidates };
  }

  function ready(snapshot: MdBookSnapshot, edge: any): { ok: true; sourceRev: number }
    | { ok: false; detail: string } {
    if (g.navigator?.onLine === false) return { ok: false, detail: 'offline' };
    const sourceRev = currentRev(snapshot.sourceKey);
    if (sourceRev == null) return { ok: false, detail: snapshot.sourceKey };
    for (const key of [`edge:${edge.id}`, `orgschema:${SCHEMA_ID}`, `orgpolicy:${POLICY_ID}`]) {
      if (currentRev(key) == null) return { ok: false, detail: key };
    }
    return { ok: true, sourceRev };
  }

  /* ── local provenance (F1) ─────────────────────────────────────────────
     Auto-materialize is this device acting on this device's own provider
     output. A proposal that arrived over sync carries a self-asserted
     confidence and proves nothing about its origin: a sync-space writer can
     forge one at conf 0.99 and have it silently fill a cell with no provider
     ever running. So the ids this device actually produced from the
     authenticated provider are recorded in a LOCAL-ONLY ledger — the key
     never matches the organizer sync envelope (SERVER_KEY_RE), so it is
     neither pushed nor pulled, and a sync-space writer cannot mint one.
     Proposal ids are deterministic content hashes, so a ledger entry can
     only ever authorise the exact immutable proposal this device produced,
     never a lookalike. A synced proposal stays readable, renderable and
     explicitly acceptable; it just never writes a cell on its own
     authority. A second device that runs the provider itself derives the
     same deterministic ids into its own ledger, so convergence is
     untouched. */
  const LOCAL_PROV_KEY = 'md:bookprov:';

  function localProvenanceIds(refKey: string): Set<string> {
    const raw = g.mdRead?.(LOCAL_PROV_KEY + refKey, null);
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)
      || !Array.isArray((raw as { ids?: unknown }).ids)) return new Set();
    return new Set((raw as { ids: unknown[] }).ids
      .filter((id): id is string => typeof id === 'string'));
  }

  function recordLocalProvenance(refKey: string, proposals: any[]): void {
    const ids = proposals.map((p) => p && p.id)
      .filter((id): id is string => typeof id === 'string');
    /* Union with what earlier runs recorded, never overwrite: a genuine
       rerun mints successors under new idempotency keys while the earlier
       run's proposals can still be live and review-pending, and dropping
       their ids would strand them in review-required on their own evidence.
       Only ids committed from a successful authenticated provider run ever
       enter this set, so the union widens nothing beyond the local-origin
       boundary this ledger exists to enforce. */
    const union = localProvenanceIds(refKey);
    for (const id of ids) union.add(id);
    /* A failed ledger write fails closed: auto-materialize quietly degrades
       to review-required, which is the safe side of this boundary. */
    g.mdWrite?.(LOCAL_PROV_KEY + refKey, { v: 1, at: Date.now(), ids: [...union] });
  }

  async function materializeReady(snapshot: MdBookSnapshot, proposals: any[]): Promise<void> {
    const fingerprint = mdBookFingerprint(snapshot);
    if (!fingerprint) return;
    const localIds = localProvenanceIds(snapshot.refKey);
    for (const proposal of proposals) {
      if (!localIds.has(proposal.id)) continue;
      const state = g.mdOrganizerState?.(proposal.id);
      if (!state || state.review !== 'proposed' || state.materialized
        || proposal.prov.conf < BOOK_POLICY.autofill || proposal.input.bodyHash !== fingerprint) continue;
      const plan = g.mdOrganizerPlan?.(proposal.id, {
        act: 'materialize', currentBodyHash: fingerprint,
      });
      if (!plan?.ok) continue;
      await g.mdOrganizerApply(plan, { currentBodyHash: fingerprint });
    }
  }

  function cancel(refKey: string): void {
    controllers.get(refKey)?.abort();
    controllers.delete(refKey);
  }

  function cleanup(refKey: string): { ok: boolean; status: string } {
    cancel(refKey);
    const edge = bookEdge(refKey);
    /* F2: the edge may already be gone — removed here earlier, or removed on
       another device and synced in — while proposals targeting it still
       arrive late over sync. The deterministic edge id names the target
       either way — the live edge's id, or the same id recomputed from the
       refKey when the edge is gone — so late proposals are tombstoned on
       that exact id with or without a live edge. Otherwise a post-removal
       proposal is stored live, can never
       apply, never renders, never gets tombstoned, and keeps riding sync
       outward — against Scenario 4's removal guarantee. Tombstones are
       terminal and idempotent, so repeating this on every reconcile of an
       untagged record writes nothing after the first pass. */
    const targetId = edge ? edge.id : bookEdgeId(refKey);
    const live = g.mdOrganizerProposalsForTarget?.(targetId) || [];
    const proposalsOk = g.mdOrganizerTombstoneTarget?.(targetId) !== false;
    if (!edge) {
      if (live.length && proposalsOk) g.mdSyncSoon?.(0);
      if (runs.has(refKey)) setRun(refKey, { status: 'idle' });
      return { ok: proposalsOk, status: 'absent' };
    }
    const edgeOk = g.mdMembershipRemove?.(LIBRARY_ID, refKey) !== false;
    g.mdSyncSoon?.(0);
    setRun(refKey, { status: 'idle' });
    return { ok: proposalsOk && edgeOk, status: 'removed' };
  }

  async function runProvider(snapshot: MdBookSnapshot, edge: any, sourceRev: number,
    fingerprint: string): Promise<any> {
    cancel(snapshot.refKey);
    const controller = new AbortController();
    const token = ++runSeq;
    controllers.set(snapshot.refKey, controller);
    setRun(snapshot.refKey, { status: 'running', token, fingerprint });
    let response: Response;
    try {
      response = await g.fetch('/api/organizer/book', {
        method: 'POST', credentials: 'same-origin', signal: controller.signal,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          excerpt: excerpt(snapshot.text),
          schema: { id: SCHEMA_ID, tag: 'book', v: 2, properties: PROVIDER_PROPERTIES },
          ...(snapshot.locale ? { locale: snapshot.locale } : {}),
        }),
      });
    } catch (error) {
      if (controller.signal.aborted) return { ok: false, status: 'cancelled' };
      setRun(snapshot.refKey, { status: 'failed', detail: 'provider_unavailable' });
      return { ok: false, status: 'failed' };
    } finally {
      if (controllers.get(snapshot.refKey) === controller) controllers.delete(snapshot.refKey);
    }
    if (controller.signal.aborted) return { ok: false, status: 'cancelled' };
    if (!response.ok) {
      let detail = `provider_${response.status}`;
      try { detail = (await response.json()).error || detail; } catch (e) { /* named fallback */ }
      setRun(snapshot.refKey, { status: 'failed', detail });
      return { ok: false, status: 'failed', detail };
    }
    let envelope: ReturnType<typeof providerEnvelope>;
    try { envelope = providerEnvelope(await response.json()); }
    catch (e) { envelope = null; }
    const latest = snapshots.get(snapshot.refKey);
    const latestEdge = bookEdge(snapshot.refKey);
    if (!envelope) {
      setRun(snapshot.refKey, { status: 'failed', detail: 'invalid-response' });
      return { ok: false, status: 'failed', detail: 'invalid-response' };
    }
    if (!latest || mdBookFingerprint(latest) !== fingerprint
      || !hasBookTag(latest.text) || !latestEdge || latestEdge.id !== edge.id
      || currentRev(snapshot.sourceKey) !== sourceRev) {
      setRun(snapshot.refKey, { status: 'stale', detail: 'source-changed' });
      if (latest && hasBookTag(latest.text)) void mdBookReconcile(latest, { force: true });
      return { ok: false, status: 'stale' };
    }
    const proposals = [];
    for (const candidate of envelope.candidates) {
      const result = g.mdOrganizerPropose({
        rule: BOOK_RULE,
        target: { edge: edge.id, property: candidate.propertyId },
        candidate: candidate.candidate,
        schemaId: SCHEMA_ID, policyId: POLICY_ID,
        runId: envelope.runId, bodyRev: sourceRev, bodyHash: fingerprint,
        sourceKey: snapshot.sourceKey, prov: candidate.prov,
      });
      if (result?.ok) proposals.push(result.proposal);
    }
    /* Only now — authenticated provider called, envelope validated, proposals
       committed from it — does this device claim these ids as its own. */
    recordLocalProvenance(snapshot.refKey, proposals);
    g.mdSyncSoon?.(0);
    setRun(snapshot.refKey, { status: 'ready', fingerprint });
    return { ok: true, status: 'ready', proposals };
  }

  async function mdBookReconcile(value: unknown, options: { force?: boolean } = {}): Promise<any> {
    const snapshot = cleanSnapshot(value);
    if (!snapshot) return { ok: false, status: 'invalid' };
    observedRefs.add(snapshot.refKey);
    snapshots.set(snapshot.refKey, snapshot);
    if (!hasBookTag(snapshot.text)) return cleanup(snapshot.refKey);
    const boot = mdBookBootstrap();
    if (!boot.ok) {
      setRun(snapshot.refKey, { status: 'failed', detail: boot.reason });
      return boot;
    }
    let edge = bookEdge(snapshot.refKey);
    if (!edge) {
      if (!g.mdMembershipAddWithId?.(LIBRARY_ID, snapshot.refKey, bookEdgeId(snapshot.refKey))) {
        setRun(snapshot.refKey, { status: 'failed', detail: 'membership' });
        return { ok: false, status: 'failed' };
      }
      edge = bookEdge(snapshot.refKey);
      g.mdSyncSoon?.(0);
    }
    if (!edge) return { ok: false, status: 'failed' };
    const fingerprint = mdBookFingerprint(snapshot) as string;
    const proposals = g.mdOrganizerProposalsForTarget?.(edge.id) || [];
    const exact = proposals.filter((proposal: any) => proposal.input.bodyHash === fingerprint
      && proposal.input.sourceKey === snapshot.sourceKey);
    if (exact.length && !options.force) {
      await materializeReady(snapshot, exact);
      setRun(snapshot.refKey, { status: 'ready', fingerprint });
      return { ok: true, status: 'ready', edge, proposals: exact };
    }
    if (runs.get(snapshot.refKey)?.status === 'running' && !options.force) {
      return { ok: true, status: 'running', edge };
    }
    const readiness = ready(snapshot, edge);
    if (!readiness.ok) {
      g.mdSyncSoon?.(0);
      setRun(snapshot.refKey, { status: 'pending', detail: readiness.detail, fingerprint });
      return { ok: true, status: 'pending', edge };
    }
    return runProvider(snapshot, edge, readiness.sourceRev, fingerprint);
  }

  async function mdBookReconcileAll(values: unknown): Promise<any[]> {
    if (!Array.isArray(values)) return [];
    const clean = values.map(cleanSnapshot).filter(Boolean) as MdBookSnapshot[];
    const present = new Set(clean.map((item) => item.refKey));
    for (const refKey of observedRefs) {
      if (!present.has(refKey)) {
        cleanup(refKey);
        snapshots.delete(refKey);
        observedRefs.delete(refKey);
      }
    }
    return Promise.all(clean.map((item) => mdBookReconcile(item)));
  }

  function retryPending(): void {
    for (const [refKey, state] of runs) {
      if (state.status !== 'pending') continue;
      const snapshot = snapshots.get(refKey);
      if (snapshot) void mdBookReconcile(snapshot);
    }
  }

  if (typeof g.addEventListener === 'function') {
    if (typeof g.__mdBookPushListener === 'function') {
      g.removeEventListener('mdsyncpushed', g.__mdBookPushListener);
    }
    if (typeof g.__mdBookOnlineListener === 'function') {
      g.removeEventListener('online', g.__mdBookOnlineListener);
    }
    g.__mdBookPushListener = retryPending;
    g.__mdBookOnlineListener = retryPending;
    g.addEventListener('mdsyncpushed', retryPending);
    g.addEventListener('online', retryPending);
  }

  function mdBookReview(refKey: unknown): any {
    if (typeof refKey !== 'string') return null;
    const snapshot = snapshots.get(refKey) || null;
    const edge = bookEdge(refKey);
    if (!edge) return { refKey, edge: null, proposals: [], run: runs.get(refKey) || { status: 'idle' } };
    const fingerprint = snapshot ? mdBookFingerprint(snapshot) : null;
    const proposals = (g.mdOrganizerProposalsForTarget?.(edge.id) || []).map((proposal: any) => ({
      ...proposal,
      state: g.mdOrganizerState?.(proposal.id),
      valid: g.mdOrganizerCandidateValidate?.(SCHEMA_ID, proposal.target.property, proposal.candidate)?.ok === true,
      stale: !!fingerprint && proposal.input.bodyHash !== fingerprint,
    }));
    return { refKey, edge, proposals, run: runs.get(refKey) || { status: 'idle' } };
  }

  async function mdBookAction(proposalId: unknown, act: unknown, currentText?: unknown): Promise<any> {
    const proposal = g.mdOrganizerProposal?.(proposalId);
    if (!proposal || typeof act !== 'string' || !['accept', 'reject', 'unlock'].includes(act)) {
      return { ok: false, refusal: 'INVALID_PROPOSAL' };
    }
    const edge = g.mdMembershipEdgeGet?.(proposal.target.edge);
    const snapshot = edge ? snapshots.get(edge.k) : null;
    if (!snapshot) return { ok: false, refusal: 'TARGET_MISSING' };
    const live = typeof currentText === 'string' ? { ...snapshot, text: currentText } : snapshot;
    snapshots.set(live.refKey, live);
    const fingerprint = mdBookFingerprint(live);
    const intent = { act, ...((act === 'accept') ? { currentBodyHash: fingerprint } : {}) };
    const plan = g.mdOrganizerPlan?.(proposal.id, intent);
    if (!plan?.ok) return plan;
    const result = await g.mdOrganizerApply(plan,
      act === 'accept' ? { currentBodyHash: fingerprint } : undefined);
    if (result?.ok) {
      g.mdSyncSoon?.(0);
      emit(live.refKey);
    }
    return result;
  }

  function mdBookRefresh(refKey: unknown, currentText?: unknown): Promise<any> {
    if (typeof refKey !== 'string') return Promise.resolve({ ok: false, status: 'invalid' });
    const snapshot = snapshots.get(refKey);
    if (!snapshot) return Promise.resolve({ ok: false, status: 'missing' });
    const live = typeof currentText === 'string' ? { ...snapshot, text: currentText } : snapshot;
    snapshots.set(refKey, live);
    return mdBookReconcile(live, { force: true });
  }

  Object.assign(g, {
    MD_BOOK_LIBRARY_ID: LIBRARY_ID,
    MD_BOOK_SCHEMA_ID: SCHEMA_ID,
    MD_BOOK_POLICY_ID: POLICY_ID,
    MD_BOOK_SCHEMA: BOOK_SCHEMA,
    MD_BOOK_POLICY: BOOK_POLICY,
    mdBookBootstrap, mdBookFingerprint, mdBookReconcile, mdBookReconcileAll,
    mdBookReview, mdBookAction, mdBookRefresh,
  });

  const mod = (globalThis as { module?: { exports?: unknown } }).module;
  if (mod && mod.exports) mod.exports = {
    MD_BOOK_LIBRARY_ID: LIBRARY_ID, MD_BOOK_SCHEMA_ID: SCHEMA_ID,
    MD_BOOK_POLICY_ID: POLICY_ID, MD_BOOK_SCHEMA: BOOK_SCHEMA, MD_BOOK_POLICY: BOOK_POLICY,
    mdBookBootstrap, mdBookFingerprint, mdBookReconcile, mdBookReconcileAll,
    mdBookReview, mdBookAction, mdBookRefresh,
  };
})(typeof window !== 'undefined' ? window : globalThis);
